import {
    api,
    kindIcon,
    useAuthStore,
    useIsPlatformHost,
    useNotificationUnreadCount,
} from '@bemor/web-kit';
import {
    AccountMenu,
    AppShell,
    BackIcon as ArrowLeft,
    PayoutIcon as Banknote,
    NotificationsIcon as Bell,
    NotificationsActiveIcon as BellRing,
    BuildingIcon as Building2,
    Button,
    ApprovalsIcon as CheckCheck,
    EmptyState,
    DocumentIcon as FileText,
    SandboxIcon as FlaskConical,
    ThresholdsIcon as Gauge,
    HomeIcon as House,
    InboxIcon as Inbox,
    Input,
    TasksIcon as ListTodo,
    OrganizationIcon,
    PageContainer,
    PeopleIcon,
    RefreshIcon as RefreshCcw,
    SearchIcon as Search,
    KycIcon as ShieldCheck,
    AlertsIcon as Siren,
    StatementIcon,
    statusLabel,
    StoreIcon,
    TenantTheme,
    TopLoader,
    OffboardingIcon as UserMinus,
    OnboardingIcon as UserPlus,
    type LucideIcon,
    type MobileTab,
    type NavEntry,
    type NavItem,
} from '@bemor/web-kit/ui';
import { useState, type FormEvent } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import type { QueueCount, QueueName } from '@/api/types';
import { P } from '@/lib/permissions';
import { useList } from '@/lib/queries';

export const QUEUES = [
    'onboarding',
    'approvals',
    'disputes',
    'alerts',
    'reconciliation',
    'kill_switches',
    'offboarding',
    'payment_reviews',
] as const;

export const queueLabel = (q: string) => statusLabel(q);

/** One icon per queue: the kind of object it holds (kit `lib/kinds`), else the step it stands for. */
const QUEUE_ICONS: Record<QueueName, LucideIcon> = {
    onboarding: UserPlus,
    approvals: kindIcon('approval'),
    disputes: kindIcon('dispute'),
    alerts: kindIcon('alert'),
    reconciliation: kindIcon('reconciliation'),
    kill_switches: kindIcon('flag'),
    offboarding: UserMinus,
    payment_reviews: kindIcon('charge'),
};

/** Queues that are also top-level nav items and tabs (design 0.6.0-c): listed once, counted once. */
const TOP_LEVEL_QUEUES: QueueName[] = ['approvals', 'alerts'];

/**
 * The staff nav (design `backoffice/shell`; BM-46 §2.1 sections after Nexroll), permission-gated, with the open
 * counts of the queues as `count`. Every item and child has its kind's icon (kit `lib/kinds`).
 */
export function buildNav(
    counts: Partial<Record<QueueName, number>> = {},
): NavEntry[] {
    const n = (q: QueueName) => counts[q] || null;
    return [
        {
            heading: 'Work',
            items: [
                { to: '/', label: 'Home', icon: kindIcon('home') },
                {
                    to: '/queues/onboarding',
                    label: 'Queues',
                    icon: kindIcon('queue'),
                    permission: P.queuesRead,
                    // BM-66 (design 0.6.0-c): Approvals and Alerts are top-level items, so they are not also queues here
                    children: QUEUES.filter(
                        (q) => !TOP_LEVEL_QUEUES.includes(q),
                    ).map((q) => ({
                        to: `/queues/${q}`,
                        label: queueLabel(q),
                        icon: QUEUE_ICONS[q],
                        count: n(q),
                    })),
                },
                {
                    to: '/cases',
                    label: 'Cases',
                    icon: kindIcon('case'),
                    permission: P.casesRead,
                },
                {
                    to: '/approvals',
                    label: 'Approvals',
                    icon: kindIcon('approval'),
                    permission: P.approvalsRead,
                    children: [
                        {
                            to: '/approvals',
                            label: 'Requests',
                            icon: Inbox,
                            count: n('approvals'),
                        },
                        {
                            to: '/approvals/rules',
                            label: 'Thresholds',
                            icon: Gauge,
                        },
                    ],
                },
                {
                    to: '/alerts',
                    label: 'Alerts',
                    icon: kindIcon('alert'),
                    permission: P.alertsRead,
                    count: n('alerts'),
                },
            ],
        },
        {
            heading: 'Merchants and money',
            items: [
                {
                    to: '/merchants',
                    label: 'Merchants',
                    icon: kindIcon('merchant'),
                    permission: P.merchantsRead,
                },
                // BM-93 decision 10: the hierarchy beside Merchants
                {
                    to: '/organizations',
                    label: 'Organizations',
                    icon: OrganizationIcon,
                    permission: P.merchantsRead,
                },
                {
                    to: '/stores',
                    label: 'Stores',
                    icon: StoreIcon,
                    permission: P.merchantsRead,
                },
                {
                    to: '/users',
                    label: 'Users',
                    icon: PeopleIcon,
                    permission: P.merchantsRead,
                },
                {
                    to: '/money/refunds',
                    label: 'Money',
                    icon: kindIcon('balance'),
                    children: [
                        {
                            to: '/money/refunds',
                            label: 'Refunds',
                            icon: kindIcon('refund'),
                            permission: P.chargesRead,
                        },
                        {
                            to: '/money/charges',
                            label: 'Charges',
                            icon: kindIcon('charge'),
                            permission: P.chargesRead,
                        },
                        // bm-147 § 1: every merchant's orders, after Charges (tenant board only: not in PLATFORM_PATHS)
                        {
                            to: '/orders',
                            label: 'Orders',
                            icon: kindIcon('order'),
                            permission: P.ordersRead,
                        },
                        // bm-147 § 5: every merchant's subscriptions, after Orders (tenant board only: not in PLATFORM_PATHS)
                        {
                            to: '/subscriptions',
                            label: 'Subscriptions',
                            icon: kindIcon('subscription'),
                            permission: P.subscriptionsRead,
                        },
                        // bm-111 § 10: every merchant's checkout sessions, beside Charges
                        {
                            to: '/sessions',
                            label: 'Sessions',
                            icon: kindIcon('checkout'),
                            permission: P.sessionsRead,
                        },
                        // bm-111 § 21: every merchant's saved payment methods, in the Charges group after Sessions (Refunds and Sessions already sit on either side of Charges)
                        {
                            to: '/payment-methods',
                            label: 'Payment methods',
                            icon: kindIcon('card'),
                            permission: P.chargesRead,
                        },
                        {
                            to: '/money/tax',
                            label: 'Sales tax',
                            icon: Banknote,
                            permission: P.taxRead,
                        },
                        // BM-44: balances, holds and adjustments live in ledger-admin; Ledger decides what each person sees.
                        // BM-109 (C2, C3): the route hands the session off to the board (this tenant's, or the platform's Ledger host)
                        {
                            to: '/ledger',
                            label: 'Open Ledger',
                            icon: kindIcon('entry'),
                        },
                    ],
                },
                {
                    to: '/pricing/cards',
                    label: 'Pricing',
                    icon: kindIcon('pricing_card'),
                    children: [
                        {
                            to: '/pricing/cards',
                            label: 'Cards',
                            icon: kindIcon('pricing_card'),
                            permission: P.pricingRead,
                        },
                        {
                            to: '/pricing/reserve-policies',
                            label: 'Reserve policies',
                            icon: kindIcon('hold'),
                            permission: P.pricingRead,
                        },
                        {
                            to: '/pricing/reserve-schedule',
                            label: 'Reserve schedule',
                            icon: kindIcon('hold'),
                            permission: P.pricingRead,
                        },
                        {
                            to: '/pricing/bin-imports',
                            label: 'BIN ranges',
                            icon: kindIcon('charge'),
                            permission: P.pricingRead,
                        },
                    ],
                },
                {
                    to: '/disputes',
                    label: 'Disputes',
                    icon: kindIcon('dispute'),
                    permission: P.disputesRead,
                },
                // BM-125: the risk engine; the platform host edits the platform rules, a tenant board its own profile
                // slice 2: Review is the one queue of held payments (decision 20); the group shows for anyone with a child
                {
                    to: '/risk/reviews',
                    label: 'Risk',
                    icon: ShieldCheck,
                    children: [
                        {
                            to: '/risk/reviews',
                            label: 'Review',
                            icon: kindIcon('charge'),
                            permission: P.riskReviewsDecide,
                        },
                        // slice 3 (decision 16): merchant monitoring and the dashboard
                        {
                            to: '/risk/monitoring',
                            label: 'Monitoring',
                            icon: Gauge,
                            permission: P.riskMonitoringRead,
                        },
                        {
                            to: '/risk/dashboard',
                            label: 'Dashboard',
                            icon: Gauge,
                            permission: P.riskMonitoringRead,
                        },
                        {
                            to: '/risk/rules',
                            label: 'Rules',
                            icon: ShieldCheck,
                            permission: P.riskRulesRead,
                        },
                        {
                            to: '/risk/profiles',
                            label: 'Profiles',
                            icon: Gauge,
                            permission: P.riskRulesRead,
                        },
                        {
                            to: '/risk/lists',
                            label: 'Lists',
                            icon: ListTodo,
                            permission: P.riskRulesRead,
                        },
                        {
                            to: '/risk/fraud-codes',
                            label: 'Fraud codes',
                            icon: kindIcon('dispute'),
                            permission: P.riskRulesRead,
                        },
                        // slice 5 (decision 45): the trained models
                        {
                            to: '/risk/models',
                            label: 'Models',
                            icon: Gauge,
                            permission: P.riskModelsManage,
                        },
                        {
                            to: '/risk/screening',
                            label: 'Screening',
                            icon: ShieldCheck,
                            permission: P.riskScreeningDecide,
                        },
                        {
                            to: '/risk/feeds',
                            label: 'Feeds',
                            icon: RefreshCcw,
                            permission: P.riskScreeningManage,
                        },
                    ],
                },
                // BM-70 § 13 (D-20 "Preview checkout"): opens checkout for a Merchant on the staff member's allow-list
                {
                    to: '/preview-checkout',
                    label: 'Preview checkout',
                    icon: FlaskConical,
                    permission: P.consoleUse,
                },
            ],
        },
        // BM-122: the finance report (cost and margin behind reporting.finance.read) and the metrics page
        {
            heading: 'Reports',
            items: [
                {
                    to: '/reports/finance',
                    label: 'Finance',
                    icon: StatementIcon,
                    permission: P.financeRead,
                },
                {
                    to: '/reports/metrics',
                    label: 'Metrics',
                    icon: Gauge,
                    permission: P.reportsRead,
                },
                // BM-136: the staff activity report (tenant board only: not in PLATFORM_PATHS)
                {
                    to: '/reports/staff-activity',
                    label: 'Staff activity',
                    icon: PeopleIcon,
                    permission: P.staffActivityRead,
                },
            ],
        },
        {
            heading: 'Platform',
            items: [
                {
                    to: '/flags',
                    label: 'Flags',
                    icon: kindIcon('flag'),
                    permission: P.flagsManage,
                },
                {
                    to: '/policies',
                    label: 'Policies',
                    icon: kindIcon('policy'),
                    permission: P.auditRead,
                },
                {
                    to: '/tenants',
                    label: 'Tenants',
                    icon: kindIcon('tenant'),
                    permission: P.tenantsManage,
                },
                {
                    to: '/staff',
                    label: 'Staff',
                    icon: kindIcon('staff'),
                    permission: P.staffManage,
                    children: [
                        {
                            to: '/staff',
                            label: 'Members',
                            icon: kindIcon('member'),
                        },
                        {
                            to: '/staff/roles',
                            label: 'Roles',
                            icon: kindIcon('role'),
                        },
                    ],
                },
            ],
        },
        {
            heading: 'Logs',
            items: [
                {
                    to: '/http-logs',
                    label: 'HTTP logs',
                    icon: kindIcon('http_logs'),
                    permission: P.httpLogsRead,
                },
                {
                    to: '/audit',
                    label: 'Audit log',
                    icon: kindIcon('audit'),
                    permission: P.auditRead,
                },
                // BM-103: Nexroll's Notification Logs page
                {
                    to: '/notification-logs',
                    label: 'Notification logs',
                    icon: kindIcon('notification'),
                    permission: P.notificationsRead,
                },
                // BM-103: api's Horizon dashboard, relayed by this server (a full load, not a route); api gates who may see it.
                {
                    to: '/horizon',
                    label: 'Horizon',
                    icon: kindIcon('http_logs'),
                    permission: P.httpLogsRead,
                },
            ],
        },
        {
            heading: 'Settings',
            items: [
                {
                    to: '/settings/security',
                    label: 'Settings',
                    icon: kindIcon('settings'),
                    children: [
                        {
                            to: '/settings/security',
                            label: 'Security',
                            icon: ShieldCheck,
                        },
                        {
                            to: '/settings/notifications',
                            label: 'Notifications',
                            icon: kindIcon('notification'),
                            permission: P.notificationsRead,
                        },
                        {
                            to: '/settings/templates',
                            label: 'Templates',
                            icon: kindIcon('template'),
                            permission: P.templatesManage,
                        },
                        {
                            to: '/settings/payment-systems',
                            label: 'Providers',
                            icon: kindIcon('charge'),
                            permission: P.systemsManage,
                        },
                        {
                            to: '/settings/push',
                            label: 'Push on this device',
                            icon: BellRing,
                            permission: P.notificationsRead,
                        },
                    ],
                },
            ],
        },
    ];
}

/** Every item of the nav, sections flattened (tests and lookups). */
export function navItems(entries: NavEntry[]): NavItem[] {
    return entries.flatMap((e) => ('heading' in e ? e.items : [e]));
}

/** Kept for the tests and other callers: the nav without counts. */
export const NAV: NavEntry[] = buildNav();

function buildTabs(counts: Partial<Record<QueueName, number>>): MobileTab[] {
    // BM-66 (design 0.6.0-c): Approvals and Alerts have their own tabs, so the Queues count leaves them out
    const total = (Object.keys(counts) as QueueName[])
        .filter((q) => !TOP_LEVEL_QUEUES.includes(q))
        .reduce((a, q) => a + (counts[q] ?? 0), 0);
    return [
        { to: '/', label: 'Home', icon: House },
        {
            to: '/queues/onboarding',
            label: 'Queues',
            icon: ListTodo,
            permission: P.queuesRead,
            count: total || null,
        },
        {
            to: '/approvals',
            label: 'Approvals',
            icon: CheckCheck,
            permission: P.approvalsRead,
            count: counts.approvals || null,
        },
        {
            to: '/alerts',
            label: 'Alerts',
            icon: Siren,
            permission: P.alertsRead,
            count: counts.alerts || null,
        },
        { to: '/search', label: 'Search', icon: Search, permission: P.search },
    ];
}

/** Items the session may not use are dropped, a group with no usable child goes with them, and so does an empty section. */
export function visibleNav(
    entries: NavEntry[],
    can: (permission: string) => boolean,
): NavEntry[] {
    const items = (list: NavItem[]): NavItem[] =>
        list.flatMap((item) => {
            if (item.permission && !can(item.permission)) return [];
            if (!item.children) return [item];
            const children = items(item.children);
            return children.length === 0 ? [] : [{ ...item, children }];
        });
    return entries.flatMap((e): NavEntry[] => {
        if (!('heading' in e)) return items([e]);
        const kept = items(e.items);
        return kept.length === 0 ? [] : [{ ...e, items: kept }];
    });
}

/**
 * BM-68: the pages of the platform host (spec 2.5): what is the platform's own or platform-wide. Everything else is a
 * tenant's work and lives on that tenant's board. (Reports has no page in Admin yet.)
 */
export const PLATFORM_PATHS = [
    '/',
    '/alerts',
    '/approvals/rules',
    '/flags',
    '/policies',
    '/tenants',
    '/staff',
    '/audit',
    '/http-logs',
    '/notifications',
    // BM-105: the platform's own notification rows (tenant-less) and api's Horizon are platform pages too
    '/notification-logs',
    '/horizon',
    // BM-105 (isolation review): platform staff keep their own preferences and devices; the platform edits the
    // template defaults; a kill switch flipped here opens the platform's own case in its kill-switches queue
    '/settings/notifications',
    '/settings/push',
    '/settings/templates',
    '/queues/kill_switches',
    '/cases',
    '/settings/security',
    // BM-62: the BIN table is shared by every tenant, and its import is a platform-only permission
    '/pricing/bin-imports',
    // BM-109 (C3): the hand-off into the platform's own Ledger host
    '/ledger',
    // BM-125: the platform's default rules, profile, lists and fraud codes are edited here; the feeds are platform data only
    '/risk/rules',
    '/risk/profiles',
    '/risk/lists',
    '/risk/fraud-codes',
    '/risk/feeds',
    // BM-125 slice 5: the platform model is activated on the platform host
    '/risk/models',
] as const;

/** BM-105: pages that are the platform's alone; a tenant board never lists them (their APIs refuse a tenant host). */
export const PLATFORM_ONLY_PATHS = [
    '/tenants',
    '/pricing/bin-imports',
    '/horizon',
    // BM-125: a lower level never writes a higher level's data; the feeds are refreshed on the platform host only
    '/risk/feeds',
] as const;

export function isPlatformOnlyPath(pathname: string): boolean {
    return PLATFORM_ONLY_PATHS.some(
        (base) => pathname === base || pathname.startsWith(`${base}/`),
    );
}

/** BM-109 (D1): a group left with one child is that child, flat; a group with none goes; the rest keeps its children. */
function keepGroup(item: NavItem, children: NavItem[]): NavItem[] {
    if (children.length === 0) return [];
    if (children.length === 1) return [children[0]];
    return [{ ...item, to: children[0].to, children }];
}

/** The nav on a tenant board: everything but the platform's own pages; a group keeps the children that stay. */
export function tenantHostNav(entries: NavEntry[]): NavEntry[] {
    const items = (list: NavItem[]): NavItem[] =>
        list.flatMap((item) => {
            if (item.children) return keepGroup(item, items(item.children));
            return isPlatformOnlyPath(item.to) ? [] : [item];
        });
    return entries.flatMap((e): NavEntry[] => {
        if (!('heading' in e)) return items([e]);
        const kept = items(e.items);
        return kept.length === 0 ? [] : [{ ...e, items: kept }];
    });
}

export function isPlatformPath(pathname: string): boolean {
    return PLATFORM_PATHS.some(
        (base) =>
            pathname === base ||
            (base !== '/' && pathname.startsWith(`${base}/`)),
    );
}

/** The nav on the platform host: only the platform pages; a group keeps the children that stay, a section with nothing goes. */
export function platformHostNav(entries: NavEntry[]): NavEntry[] {
    const items = (list: NavItem[]): NavItem[] =>
        list.flatMap((item) => {
            if (item.children) return keepGroup(item, items(item.children));
            return isPlatformPath(item.to) ? [item] : [];
        });
    return entries.flatMap((e): NavEntry[] => {
        if (!('heading' in e)) return items([e]);
        const kept = items(e.items);
        return kept.length === 0 ? [] : [{ ...e, items: kept }];
    });
}

/** A tenant page asked for on the platform host: it lives on the tenant's board. */
function PlatformOnly() {
    return (
        <div
            className="border-border-1 bg-surface-card rounded-lg border"
            data-testid="platform-only"
        >
            <EmptyState
                icon={Building2}
                title="This page belongs to a tenant's board"
                body="The platform host holds the platform's own pages. Open a tenant from Tenants to work on its queues, merchants and money."
                action={
                    <Button asChild variant="secondary">
                        <Link to="/tenants">Tenants</Link>
                    </Button>
                }
            />
        </div>
    );
}

function SearchBox() {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const submit = (e: FormEvent) => {
        e.preventDefault();
        if (q.trim().length >= 3)
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    };
    return (
        <form
            method="post"
            onSubmit={submit}
            className="relative hidden min-w-0 flex-1 md:block md:max-w-60 xl:max-w-80"
            role="search"
        >
            <Search
                className="text-fg-2 pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2"
                aria-hidden="true"
            />
            <Input
                aria-label="Search"
                placeholder="Search ids, last four, names"
                value={q}
                enterKeyHint="search"
                onChange={(e) => setQ(e.target.value)}
                className="h-8 pl-8 text-sm"
            />
        </form>
    );
}

function BellLink() {
    const unread = useNotificationUnreadCount();
    const n = unread.data ?? 0;
    return (
        <Link
            to="/notifications"
            className="hover:bg-surface-hover text-fg-1 relative inline-flex size-10 items-center justify-center rounded-md"
            aria-label={n > 0 ? `Notifications, ${n} unread` : 'Notifications'}
        >
            <Bell className="size-4.5" aria-hidden="true" />
            {n > 0 ? (
                <span
                    aria-hidden="true"
                    className="bg-signal-500 absolute top-1 right-1 min-w-4 rounded-full px-1 text-center text-xs leading-4 font-semibold text-white"
                    data-testid="unread-badge"
                >
                    {n}
                </span>
            ) : null}
        </Link>
    );
}

/** Ends the session on the app's server (which tells Identity), clears the store and goes to sign-in. */
export async function signOut(
    navigate: (to: string, o?: { replace?: boolean }) => void,
) {
    await fetch('/auth/logout', { method: 'POST' }).catch(() => undefined);
    api();
    useAuthStore.getState().clear();
    navigate('/login', { replace: true });
}

/**
 * The one account menu (design 0.6.0-a, kit `AccountMenu`): name and email, Security, Sign out. BM-68: on a tenant
 * board a platform staff member also gets "Back to platform" (`PLATFORM_ADMIN_URL`).
 */
function UserMenu() {
    const session = useAuthStore((s) => s.session);
    const platformAdminUrl = useAuthStore((s) => s.links?.platformAdmin);
    const platformHost = useIsPlatformHost();
    const navigate = useNavigate();
    const backToPlatform =
        !platformHost && session?.tenantScope === 'platform' && platformAdminUrl
            ? [
                  {
                      label: 'Back to platform',
                      icon: ArrowLeft,
                      href: platformAdminUrl,
                  },
              ]
            : [];
    return (
        <AccountMenu
            items={backToPlatform}
            person={{ name: session?.actor.name, email: session?.actor.email }}
            role={
                session?.actor.email && session.actor.name
                    ? session.actor.email
                    : null
            }
            onSecurity={() => navigate('/settings/security')}
            onSignOut={() => void signOut(navigate)}
        />
    );
}

/**
 * The signed-in chrome (design `backoffice/shell`): the kit's `AppShell` in the Staff theme with Signal marking the
 * active item and the counts, the pin remembered per staff member, the queue counts on the nav and the tabs, and the
 * header's search, bell and account menu (Security, Sign out). BM-68: on the platform host (no tenant behind it) the
 * mark is the platform's brand and the nav holds the platform pages only; on a tenant board a platform staff member
 * gets "Back to platform" in the account menu.
 */
export function AppLayout() {
    const tenant = useAuthStore((s) => s.tenant);
    const platform = useAuthStore((s) => s.platform);
    const session = useAuthStore((s) => s.session);
    const can = useAuthStore((s) => s.can);
    const platformHost = useIsPlatformHost();
    const { pathname } = useLocation();
    // the queues are a tenant's work; BM-105: the platform host counts its own (the kill-switches queue), which
    // row-level security answers with the platform's rows alone
    const queues = useList<QueueCount>('/staff/queues', {}, can(P.queuesRead));
    const counts: Partial<Record<QueueName, number>> = {};
    for (const q of queues.data?.data.items ?? []) counts[q.queue] = q.open;
    const fullNav = visibleNav(buildNav(counts), can);
    return (
        <TenantTheme brand={null} theme="staff" className="min-h-dvh">
            <AppShell
                // BM-46 §2.1: the tenant's logo, else its name (white-label); never a hard-coded Bemor. BM-68: the
                // platform's own brand on the platform host.
                brand={
                    platformHost
                        ? {
                              name: platform?.brand.display_name || 'Bemor',
                              logoUrl: platform?.brand.logo_url ?? null,
                          }
                        : {
                              name: tenant?.brand.display_name || 'Bemor',
                              logoUrl: tenant?.brand.logo_url ?? null,
                          }
                }
                brandLabel="Admin"
                topLoader={<TopLoader />}
                accent="signal"
                persistKey={session?.actor.id || undefined}
                nav={
                    platformHost
                        ? platformHostNav(fullNav)
                        : tenantHostNav(fullNav)
                }
                tabs={buildTabs(counts).filter(
                    (t) =>
                        (!t.permission || can(t.permission)) &&
                        (!platformHost || isPlatformPath(t.to)),
                )}
                can={can}
                header={
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        {can(P.search) && !platformHost ? <SearchBox /> : null}
                        <div className="flex-1" />
                        {can(P.notificationsRead) ? <BellLink /> : null}
                        <UserMenu />
                    </div>
                }
            >
                {/* BM-84 (decision D1, design 0.7.0 DbPage): lists fill to --container-list and centre, pages centre at --container-max */}
                <PageContainer>
                    {platformHost && !isPlatformPath(pathname) ? (
                        <PlatformOnly />
                    ) : (
                        <Outlet />
                    )}
                </PageContainer>
            </AppShell>
        </TenantTheme>
    );
}

export function SandboxNotice() {
    return (
        <div
            className="mx-auto max-w-md space-y-2 p-6 text-center"
            data-testid="sandbox-notice"
        >
            <FileText className="text-fg-2 mx-auto size-6" />
            <h1 className="text-lg font-semibold">Sandbox</h1>
            <p className="text-fg-2 text-sm">
                The sandbox environment arrives with the test-mode keys
                (decision 20). Until then every screen here shows live data
                only.
            </p>
        </div>
    );
}
